<script lang="ts" setup generic="Key, Item">
import type { VList } from 'vuetify/components'
import type { Density } from 'vuetify/lib/composables/density.mjs'
import type { ItemIcons, ItemKeys, TreeData } from './utree.js'
import { useVModel } from '@vueuse/core'
import { onMounted, ref, shallowRef } from 'vue'
import { useStateScope } from '../../../app/helper/shortcut.js'
import { UTreeDataSymbol, getItemChildren, getItemTitle, getItemValue, getStyle, hasChildren } from './utree.js'
import UTreeLevel from './UTreeLevel.vue'

interface UTreeProps {
  selected?: Array<Key>
  opened?: Array<Key>
  items: Array<Item>
  itemFilter?: (item: Item) => boolean
  loadChildren?: (item: Item) => Promise<void>

  disableAnimations?: boolean

  collapseIcon?: string
  expandIcon?: string
  loadingIcon?: string
  storageIcon?: string
  storageOpenedIcon?: string
  storageErrorIcon?: string
  errorIcon?: string
  emptyIcon?: string

  itemTitle?: string
  itemValue?: string
  itemChildren?: string
  itemError?: string
  itemType?: string
  itemLoading?: string
  scrollTo?: (el: HTMLElement) => void
  density?: Density
}

const props = withDefaults(defineProps<UTreeProps>(), {
  selected: () => [],
  opened: () => [],
  itemTitle: 'title',
  itemValue: 'value',
  itemChildren: 'children',
  itemError: 'error',
  itemType: 'type',
  itemLoading: 'loading',
  disableAnimations: false,
  density: 'default',
})
const emits = defineEmits(['update:selected', 'update:opened', 'click:open', 'contextmenu'])

const selected = useVModel(props, 'selected', emits)
const opened = useVModel(props, 'opened', emits)

const keys: ItemKeys = {
  itemTitle: props.itemTitle,
  itemValue: props.itemValue,
  itemChildren: props.itemChildren,
  itemError: props.itemError,
  itemType: props.itemType,
  itemLoading: props.itemLoading,
}

const list = ref<VList | null>(null)
const treeData = shallowRef<TreeData | undefined>(undefined)
provide(UTreeDataSymbol, treeData)

function slideIntoView(): void {
  nextTick(() => {
    const selectedElement = document.querySelector('.tree-view-wrapper .v-list-item--active .v-list-item__content .v-list-item-title')
    // const behavior = props.disableAnimations ? 'instant' : 'smooth'
    // selectedElement?.scroll({behavior: 'instant', top: 0, left: 0})
    // selectedElement?.scrollIntoView({ behavior, block: 'center', inline: 'nearest' })
    console.log('Slide into view', selectedElement)
    props.scrollTo?.(selectedElement as HTMLElement)
  })
}

const itemChildren = new Map<Key, Key[]>()

function fillChildren(item: Item): void {
  const children = getItemChildren(item, keys)
  if (hasChildren(children)) {
    itemChildren.set(getItemValue(item, keys), children.map(c => getItemValue<Key, Item>(c, keys)))
    children.forEach(fillChildren)
  }
}

onMounted(() => {
  opened.value.forEach((o) => {
    const item = props.items.find(i => getItemValue(i, keys) === o)
    if (item)
      fillChildren(item)
  })
  const parents = shallowRef(list.value!.parents)
  const children = shallowRef(itemChildren)
  const icons: ItemIcons = { collapse: props.collapseIcon, expand: props.expandIcon, load: props.loadingIcon, root: props.storageIcon, rootOpened: props.storageOpenedIcon, error: props.errorIcon, empty: props.emptyIcon, rootError: props.storageErrorIcon }
  treeData.value = { parents, children, keys, icons, fillChildren: (item: unknown) => fillChildren(item as Item) }
  slideIntoView()
})

const items = computed(() => {
  return props.items
})

const stateScope = useStateScope()

defineExpose({ slideIntoView, list })
</script>

<template>
  <v-list
    ref="list"
    v-model:opened="opened"
    v-model:activated="selected"
    class="u-tree"
    :disabled="stateScope?.isLoadingBlocking.value ?? false"
    activatable
    active-strategy="single-independent"
    :open-strategy="{ open: ({ opened }) => opened, select: () => null }"
    expand-icon=""
    collapse-icon=""
    mandatory
  >
    <template
      v-for="(item, i) of items"
    >
      <UTreeLevel
        v-if="hasChildren(getItemChildren(item, keys))"
        :key="i"
        v-model:selected="selected"
        v-model:opened="opened"
        :density="density"
        :load-children="loadChildren"
        :item-filter="itemFilter"
        :group-item="item"
        :items="getItemChildren(item, keys)"
        :level="1"
        :scroll-to="scrollTo"
      >
        <template #item="{ it, style }">
          <slot name="item" v-bind="{ it, style }">
            <v-list-item
              :title="getItemTitle(it, keys)"
              :value="getItemValue(it, keys)"
              :style="style"
              :density="density"
            />
          </slot>
        </template>
      </UTreeLevel>
      <slot v-else name="item" v-bind="{ it: item, style: getStyle(0) }">
        <v-list-item
          :key="i"
          :title="getItemTitle(item, keys)"
          :value="getItemValue(item, keys)"
          :style="getStyle(0)"
          :density="density"
        />
      </slot>
    </template>
  </v-list>
</template>

<style>
.u-tree {
  .v-list-item {
    min-width: 20rem;
  }
}
</style>
